import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useLoyaltyPointsPurchaseBestOffer from 'loyalty/hooks/useLoyaltyPointsPurchaseBestOffer'

const Card = styled(Pane)`
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(16)};
  text-align: center;
`

interface Props {
  rewardPointsDeficit: number
  secondaryAction?: ReactNode
}

/**
 * Card shown when the member does not have enough loyalty points to redeem a reward fare.
 */
function InsufficientLoyaltyPointsCard({
  rewardPointsDeficit,
  secondaryAction,
}: Props) {
  const intl = useIntl()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const { offer: bestPurchaseOffer } = useLoyaltyPointsPurchaseBestOffer(rewardPointsDeficit)
  const buyPointsHref = bestPurchaseOffer
    ? `/societe/buy-points?offerId=${encodeURIComponent(bestPurchaseOffer.id)}`
    : '/societe/buy-points'

  return (
    <Card background="light-background">
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="center" gap={4}>
          <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
          <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">
            {intl.formatMessage(
              {
                id: 'HK_aIz5NHfsk81_smQ0n',
                defaultMessage: '{loyaltyProgramName} Rewards',
              },
              { loyaltyProgramName },
            )}
          </BodyText>
        </Group>
        <BodyText variant="small" colour="neutral-one">
          <FormattedMessage
            id="8SXh6AphzYe1LJP91FJA"
            defaultMessage="You require an additional {points} to book this package."
            
            values={{ points: <FormatLoyaltyPoints points={rewardPointsDeficit} /> }}
          />
        </BodyText>
        <TextButton kind="primary" fit="full-width" to={buyPointsHref} size="small" target="_blank">
          {intl.formatMessage(
            {
              id: '87a9q3FLljsEErQFggqK',
              defaultMessage: 'Top-up {loyaltyProgramName} Points',
            },
            { loyaltyProgramName },
          )}
        </TextButton>
        {secondaryAction}
      </Group>
    </Card>
  )
}

export default InsufficientLoyaltyPointsCard
